import React, { useState, useEffect, useContext, useRef } from "react";
import toast from "react-hot-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AddSupplier from "../components/AddSupplier";
import EditSupplier from "../components/EditSupplier";
import AuthContext from "../AuthContext";
import { privateAxios } from "../utils/axios";

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

function Store() {
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const limitOptions = [5, 10, 20, 50, 100];
  const searchInputRef = useRef(null);

  const authContext = useContext(AuthContext);
  const queryClient = useQueryClient();

  // Debounce the search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // 500ms delay

  // Fetch suppliers with React Query
  const {
    data: suppliersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["suppliers", currentPage, debouncedSearchTerm, limit],
    queryFn: async () => {
      const response = await privateAxios.get(
        `/suppliers/?limit=${limit}&page=${currentPage}&q=${debouncedSearchTerm}`
      );
      if (!response.data.success) {
        throw new Error("Network response was not ok");
      }
      return response.data;
    },
  });

  // Keep focus on search input after data fetch
  useEffect(() => {
    if (
      searchInputRef.current &&
      document.activeElement !== searchInputRef.current
    ) {
      searchInputRef.current.focus();
    }
  }, [suppliersData]);

  const modalSetting = () => {
    setShowModal(!showModal);
  };

  const handleEdit = (supplier) => {
    setSelectedSupplier(supplier);
    setShowEditModal(true);
  };

  const editModalSetting = () => {
    setShowEditModal(false);
    setSelectedSupplier(null);
  };

  // Handle Search Term
  const handleSearchTerm = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  // Handle Page Change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // When limit changes, reset to first page
  useEffect(() => {
    setCurrentPage(1);
  }, [limit]);

  if (error) return <div>Error: {error.message}</div>;
const deleteSupplier = async (id) => {
  try {
    const response = await privateAxios.delete(`/suppliers/${id}`);

    if (response.data.success) {
      toast.success("Supplier deleted successfully");
      queryClient.invalidateQueries(["suppliers"]);
    } else {
      toast.error(response.data.message || "Failed to delete supplier");
    }
  } catch (err) {
    console.error("Error deleting supplier:", err);
    toast.error(
      err?.response?.data?.message || "Error deleting supplier"
    );
  }
};

  const handleDeleteSupplierClick = async (id) => {
  const confirmed = window.confirm(
    "Are you sure you want to delete this supplier? This action cannot be undone."
  );

  if (!confirmed) return;

  await deleteSupplier(id);
};


  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center">
      <div className="flex flex-col gap-5 w-11/12">
        {/* --- Overall Store Card (new design) --- */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 w-full mt-6 mb-8">
          <div>
            <span className="font-bold text-xl text-gray-900">
              Overall suppliers
            </span>
            <div className="text-gray-500 text-sm mt-1 mb-6">
              Overview of your suppliers status
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Total Suppliers */}
            <div className="flex-1 bg-gray-50 rounded-lg flex items-center gap-4 p-5">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100">
                {/* Store icon */}
                <svg
                  className="w-6 h-6 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />
                </svg>
              </div>
              <div>
                <div className="text-gray-500 text-sm">Total Suppliers</div>
                <div className="font-bold text-2xl text-gray-900">
                  {suppliersData?.data?.suppliers?.length || 0}
                </div>
              </div>
            </div>
          </div>
        </div>

        {showModal && (
          <AddSupplier
            addSupplierModalSetting={modalSetting}
            handlePageUpdate={() =>
              queryClient.invalidateQueries(["suppliers"])
            }
          />
        )}

        {showEditModal && selectedSupplier && (
          <EditSupplier
            supplier={selectedSupplier}
            editSupplierModalSetting={editModalSetting}
            handlePageUpdate={() =>
              queryClient.invalidateQueries(["suppliers"])
            }
          />
        )}
        

        {/* Table */}
        <div className="overflow-x-auto rounded-lg border bg-white border-gray-200 mb-10">
          <div className="flex justify-between pt-5 pb-3 px-3 flex-col md:flex-row md:items-center my-3">
            <div className="flex gap-4 justify-center items-center w-full md:w-auto mb-4 md:mb-0">
              <span className="font-bold text-lg">Suppliers</span>
              <div className="relative flex items-center px-2 border-2 rounded-md shadow-sm w-80 bg-white">
                <img
                  alt="search-icon"
                  className="w-5 h-5"
                  src={require("../assets/search-icon.png")}
                />
                <input
                  ref={searchInputRef}
                  className="border-none outline-none focus:outline-none focus:ring-0 focus:border-none text-base pl-2 pr-6 bg-transparent w-full"
                  type="text"
                  placeholder="Search here"
                  value={searchTerm}
                  onChange={handleSearchTerm}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="absolute right-2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    onClick={() => {
                      setSearchTerm("");
                      searchInputRef.current?.focus();
                    }}
                    tabIndex={-1}
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div className="flex gap-4 justify-end w-full md:w-auto">
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6 py-2 text-base rounded-lg transition-all duration-200 min-w-[160px]"
                onClick={modalSetting}
              >
                Add Supplier
              </button>
            </div>
          </div>
          <table className="min-w-full divide-y divide-gray-200 text-sm overflow-hidden shadow-sm border">
            <thead>
              <tr className="bg-gray-200 border-b">
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[8%] text-sm">
                  S.No
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[15%] text-sm">
                
                    Store Name
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[15%] text-sm">
                  Supplier Name
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[12%] text-sm">
                  Phone
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[15%] text-sm">
                  Email
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[20%] text-sm">
                  Address
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[10%] text-sm">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                      <span className="ml-2 text-sm text-gray-500">
                        Loading suppliers...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : suppliersData?.data?.suppliers?.length > 0 ? (
                suppliersData.data.suppliers.map((supplier, index) => (
                  <tr
                    key={supplier._id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                      {(currentPage - 1) * limit + index + 1}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-900 capitalize text-sm">
                    
                         {supplier.storeName}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                     {supplier.name}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                      {supplier.phone}
                      {supplier.phone2 && (
                        <span className="text-gray-500 text-xs block">
                          Alt: {supplier.phone2}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                      {supplier.email || "-"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                      {supplier.address}
                    </td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(supplier)}
                        className="bg-blue-500 hover:bg-blue-600 text-white font-medium px-2.5 py-0.5 rounded text-xs transition-colors duration-200"
                      >
                        Edit
                      </button>

                      <button
                        onClick={() => handleDeleteSupplierClick(supplier._id)}
                        className="bg-red-500 hover:bg-red-600 text-white font-medium px-2.5 py-0.5 rounded text-xs transition-colors duration-200"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    className="px-4 py-8 text-center text-sm text-gray-500"
                  >
                    No suppliers found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination - bottom right */}
          <div className="flex justify-end items-center gap-4 py-5 pr-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
                className="px-4 py-2 rounded border text-base disabled:opacity-50 hover:bg-gray-50 transition-all duration-200"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-base">
                Page {currentPage} of {suppliersData?.data?.totalPages || 1}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={
                  currentPage === (suppliersData?.data?.totalPages || 1) ||
                  isLoading
                }
                className="px-4 py-2 rounded border text-base disabled:opacity-50 hover:bg-gray-50 transition-all duration-200"
              >
                Next
              </button>
            </div>
            <select
              className="ml-6 px-3 py-2 border rounded text-base min-w-[120px] z-10 bg-white"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              {limitOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt} / page
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Store;
